import { DEFAULT_DATE_RANGE_DAYS } from './constants';
import { TranscriptMessage } from 'types';
import { CALL_DURATION_UNIT, CallDurationUnit } from 'constants/deployment';

/**
 * Get default date range for data pages
 * Centralized function to ensure consistency across all components
 *
 * Uses LOCAL timezone to ensure "today" matches user's local date
 * For India (UTC+5:30): If it's Oct 14 in India, returns Oct 14 (not Oct 13 UTC)
 *
 * To change default range:
 * - Operational pages (CallLogs, AudioSessions, Transfers): modify DEFAULT_DATE_RANGE_DAYS in constants.ts
 * - Dashboard: modify DASHBOARD_DATE_RANGE_DAYS in constants.ts
 *
 * @param days - Number of days to include (default: DEFAULT_DATE_RANGE_DAYS = 2)
 * @returns Object with dateFrom and dateTo in YYYY-MM-DDTHH:mm format (datetime-local)
 */
export const getDefaultDateRange = (days: number = DEFAULT_DATE_RANGE_DAYS) => {
  const today = new Date();
  const startDate = new Date();
  startDate.setDate(today.getDate() - (days - 1)); // Last N days including today

  // Set start date to 00:00:00
  startDate.setHours(0, 0, 0, 0);
  // Set end date to 23:59:59
  today.setHours(23, 59, 59, 999);

  // Format datetime in local timezone (YYYY-MM-DDTHH:mm)
  const formatLocalDateTime = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  return {
    dateFrom: formatLocalDateTime(startDate),
    dateTo: formatLocalDateTime(today)
  };
};

/**
 * A call's duration, which arrives in SECONDS.
 *
 * The switch stores `duration` and `billableseconds` in milliseconds despite
 * both column names saying otherwise, and the API used to pass that straight
 * through — so every call site here divided by 1000 via `formatDuration` and
 * the two errors cancelled out. The API now converts to real seconds, which
 * made that cancellation into a bug: a 115-second call rendered as "0:00".
 *
 * Kept separate from `formatDuration` because that one's other callers
 * (`duration_ms`, `audio_duration_ms`) genuinely are milliseconds. One
 * formatter guessing at units is how this happened the first time.
 */
/**
 * A call's length as m:ss.
 *
 * The unit of `call.duration` depends on which backend answered — cpass returns
 * seconds, the older tvoice backend returns the raw switch value in
 * milliseconds — so it is resolved per deployment rather than assumed. Pass the
 * unit explicitly to override it (tests do).
 */
export const formatCallDuration = (
  value?: number | null,
  unit: CallDurationUnit = CALL_DURATION_UNIT,
): string => {
  if (value === undefined || value === null) return '-';
  const seconds = unit === 'milliseconds' ? value / 1000 : value;
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

export const formatDuration = (milliseconds: number): string => {
  // Convert milliseconds to seconds
  const totalSeconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

/**
 * Format duration dynamically based on magnitude
 * - < 60 seconds: show in seconds (e.g., "45s")
 * - 60-3599 seconds: show in minutes and seconds (e.g., "5m 30s")
 * - >= 3600 seconds: show in hours and minutes (e.g., "2h 15m")
 */
export const formatDurationSmart = (milliseconds: number): string => {
  const totalSeconds = Math.floor(milliseconds / 1000);

  if (totalSeconds < 60) {
    // Less than 1 minute: show seconds
    return `${totalSeconds}s`;
  } else if (totalSeconds < 3600) {
    // Less than 1 hour: show minutes and seconds
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  } else {
    // 1 hour or more: show hours and minutes
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
};

export const formatBytes = (bytes: number): string => {
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(2)} MB`;
};

/**
 * Format large numbers in K format (thousands)
 * - < 1000: show as is (e.g., "999")
 * - >= 1000: show in K format (e.g., "5K", "5.5K", "15K")
 *
 * Examples:
 *   500 → "500"
 *   1000 → "1K"
 *   1500 → "1.5K"
 *   5000 → "5K"
 *   15300 → "15.3K"
 */
export const formatNumberWithK = (num: number): string => {
  if (num < 1000) {
    return num.toString();
  }

  const thousands = num / 1000;
  // If it's a whole number (like 5000 → 5K), don't show decimal
  if (thousands % 1 === 0) {
    return `${thousands}K`;
  }
  // Otherwise show one decimal place (like 5500 → 5.5K)
  return `${thousands.toFixed(1)}K`;
};

/**
 * A backend timestamp, read as the UTC instant it actually is.
 *
 * <h3>The bug this fixes</h3>
 * The backend emits `LocalDateTime.toString()` — "2026-09-19T07:10:25", with no
 * Z and no offset — from a JVM pinned to UTC. JavaScript parses a date-time
 * string WITHOUT an offset as LOCAL time, so a caller in India saw 07:10 for a
 * call that happened at 12:40 IST. The clock reading passed through untouched,
 * which looks exactly like "the UI shows UTC".
 *
 * The comment that used to sit here said this function converts UTC to the
 * browser's timezone. It could not: the input never said it was UTC.
 *
 * <h3>Why treat a zone-less string as UTC rather than local</h3>
 * Every timestamp reaching this app comes from a backend running with
 * -Duser.timezone=UTC. There is no source of local-time strings, so the
 * ambiguity has exactly one correct reading — and guessing "local" is what
 * produced the wrong answer.
 *
 * A string that DOES carry a zone (…Z or …+05:30) is left alone, so anything
 * already unambiguous keeps working.
 */
const asUtcInstant = (value: Date | string): Date => {
  if (value instanceof Date) return value;
  const s = String(value).trim();
  // A bare date, or a date-time already carrying Z / ±hh:mm, is unambiguous.
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(s);
  const isDateTime = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(s);
  if (!isDateTime || hasZone) return new Date(s);
  // "2026-09-19 07:10:25" is valid on some engines and not others; normalise
  // the separator before declaring the zone.
  return new Date(s.replace(' ', 'T') + 'Z');
};

/**
 * Format date/time for display in the viewer's local timezone.
 *
 * Examples, for a backend value of 2026-09-19T07:10:25 (UTC):
 *   India (UTC+5:30):   "19/09/2026, 12:40:25 pm"
 *   London (UTC+1):     "19/09/2026, 08:10:25"
 *   New York (UTC-4):   "9/19/2026, 3:10:25 AM"
 *
 * TODO: honour UserPreferences.timezone instead of the browser's. That
 * preference exists, defaults to "UTC", is saved from Settings — and nothing
 * reads it, so saving it changes nothing. Whose timezone a shared portal should
 * use is a product decision, not a formatting one.
 *
 * @param date - Date object or backend timestamp string
 * @returns Formatted date string, or empty string if invalid
 */
export const formatDate = (date: Date | string): string => {
  if (!date) return '';
  const d = asUtcInstant(date);
  // Check if date is valid
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString();
};

/**
 * Format epoch timestamp for display in user's LOCAL timezone
 * Handles both seconds and milliseconds format automatically
 *
 * Use this for transfer data and other APIs that return epoch timestamps as numbers
 *
 * @param timestamp - Epoch timestamp in seconds or milliseconds (number)
 * @returns Formatted date string in user's local timezone, or empty string if invalid
 */
/**
 * Accepts an epoch (seconds or milliseconds) OR an ISO datetime string.
 *
 * The string case is not hypothetical: the federated call and transfer
 * endpoints return `transfer_time` / `start_time` as
 * `"2026-08-29T13:47:28"`, not as an epoch. This used to be typed
 * `number` only, so those callers type-checked while passing a string —
 * it happened to work, because comparing a string to a number yields
 * false and `new Date(isoString)` parses, but only by accident.
 */
export const formatTimestamp = (timestamp: number | string | null | undefined): string => {
  if (timestamp === null || timestamp === undefined || timestamp === '') return '';

  if (typeof timestamp === 'string') {
    // A numeric string is an epoch that arrived as text; anything else is ISO.
    const asNumber = Number(timestamp);
    if (!Number.isNaN(asNumber) && timestamp.trim() !== '') {
      return formatTimestamp(asNumber);
    }
    const parsed = new Date(timestamp);
    return isNaN(parsed.getTime()) ? '' : parsed.toLocaleString();
  }

  if (timestamp <= 0) return '';

  // Convert to milliseconds if timestamp is in seconds (< 10 billion is seconds)
  // Epoch seconds will be less than 10,000,000,000 until year 2286
  // Scientific notation like 1.7606777627769482E9 is in seconds
  const timestampMs = timestamp < 10000000000 ? timestamp * 1000 : timestamp;

  const date = new Date(timestampMs);
  // Check if date is valid
  if (isNaN(date.getTime())) return '';
  return date.toLocaleString();
};

export const getStatusColor = (status: string): string => {
  const colors: Record<string, string> = {
    answered: 'bg-green-100 text-green-800',
    completed: 'bg-green-100 text-green-800',
    missed: 'bg-red-100 text-red-800',
    busy: 'bg-yellow-100 text-yellow-800',
    initiated: 'bg-yellow-100 text-yellow-800'
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

export const getCallTypeColor = (type: string): string => {
  return type === 'IN' 
    ? 'bg-green-100 text-green-800' 
    : 'bg-purple-100 text-purple-800';
};

export const getRoleColor = (role: string): string => {
  const colors: Record<string, string> = {
    super_admin: 'bg-purple-100 text-purple-800',
    admin: 'bg-blue-100 text-blue-800',
    operator: 'bg-green-100 text-green-800'
  };
  return colors[role] || 'bg-gray-100 text-gray-800';
};

/**
 * Convert date or datetime string to epoch timestamp (milliseconds)
 * Supports both YYYY-MM-DD and YYYY-MM-DDTHH:mm formats
 * Converts to start of day (00:00:00.000) if only date provided, or specific time if datetime provided
 * All conversions in USER'S LOCAL TIMEZONE
 *
 * Example (for user in India IST = UTC+5:30):
 *   Input: "2025-10-07" → October 7, 2025 00:00:00 IST
 *   Input: "2025-10-07T14:30" → October 7, 2025 14:30:00 IST
 *   Output: Milliseconds since Unix epoch
 *
 * @param dateString - Date in YYYY-MM-DD or YYYY-MM-DDTHH:mm format
 * @returns Milliseconds since Unix epoch (Jan 1, 1970 00:00:00 UTC)
 */
export const dateStringToEpoch = (dateString: string): number => {
  if (!dateString) return 0;

  // Check if it includes time (YYYY-MM-DDTHH:mm format)
  if (dateString.includes('T')) {
    // Parse datetime string: "2025-10-07T14:30"
    const [datePart, timePart] = dateString.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);
    // Create date in LOCAL TIMEZONE
    const date = new Date(year, month - 1, day, hours, minutes, 0, 0);
    return date.getTime();
  } else {
    // Parse date-only string: "2025-10-07"
    const [year, month, day] = dateString.split('-').map(Number);
    // Create date in LOCAL TIMEZONE at start of day
    const date = new Date(year, month - 1, day, 0, 0, 0, 0);
    return date.getTime();
  }
};

/**
 * Convert date or datetime string to epoch timestamp for end of day/time
 * Supports both YYYY-MM-DD and YYYY-MM-DDTHH:mm formats
 * Converts to end of day (23:59:59.999) if only date provided, or specific time if datetime provided
 * All conversions in USER'S LOCAL TIMEZONE
 *
 * Example (for user in India IST = UTC+5:30):
 *   Input: "2025-10-07" → October 7, 2025 23:59:59.999 IST
 *   Input: "2025-10-07T14:30" → October 7, 2025 14:30:59.999 IST
 *   Output: Milliseconds since Unix epoch
 *
 * @param dateString - Date in YYYY-MM-DD or YYYY-MM-DDTHH:mm format
 * @returns Milliseconds since Unix epoch (Jan 1, 1970 00:00:00 UTC)
 */
export const dateStringToEpochEndOfDay = (dateString: string): number => {
  if (!dateString) return 0;

  // Check if it includes time (YYYY-MM-DDTHH:mm format)
  if (dateString.includes('T')) {
    // Parse datetime string: "2025-10-07T14:30"
    const [datePart, timePart] = dateString.split('T');
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);
    // Create date in LOCAL TIMEZONE with the specified time, add 59 seconds and 999ms
    const date = new Date(year, month - 1, day, hours, minutes, 59, 999);
    return date.getTime();
  } else {
    // Parse date-only string: "2025-10-07"
    const [year, month, day] = dateString.split('-').map(Number);
    // Create date in LOCAL TIMEZONE at end of day
    const date = new Date(year, month - 1, day, 23, 59, 59, 999);
    return date.getTime();
  }
};

/**
 * Convert epoch timestamp (milliseconds) to date string (YYYY-MM-DD)
 */
export const epochToDateString = (epoch: number): string => {
  const date = new Date(epoch);
  return date.toISOString().split('T')[0];
};

/**
 * Get current time as epoch timestamp (milliseconds)
 */
export const getCurrentEpoch = (): number => {
  return Date.now();
};

/**
 * Convert date string with time (YYYY-MM-DD HH:mm:ss) to epoch timestamp
 */
export const dateTimeStringToEpoch = (dateTimeString: string): number => {
  return new Date(dateTimeString).getTime();
};

/**
 * Format date/time in UTC timezone (for future use)
 *
 * TODO: Use this when user preference is set to "Display in UTC"
 *
 * @param date - Date object or ISO string from backend
 * @returns Formatted date string in UTC timezone
 */
export const formatDateUTC = (date: Date | string): string => {
  const d = new Date(date);
  return d.toUTCString();
};

/**
 * Format date/time with explicit timezone indicator (for future use)
 *
 * TODO: Use this to show timezone alongside time
 *
 * @param date - Date object or ISO string from backend
 * @returns Formatted date string with timezone (e.g., "10/7/2025, 9:30:00 AM EST")
 */
export const formatDateWithTimezone = (date: Date | string): string => {
  const d = new Date(date);
  const formatted = d.toLocaleString();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return `${formatted} (${timezone})`;
};

/**
 * Hide the numbers in a piece of text that identify a person.
 *
 * Used on call summaries and transcripts, which are read on screen by whoever
 * can open the call — including, before long, outsourced agents who have no
 * business holding a customer's number.
 *
 * <h3>What changed, and why</h3>
 * This was written for PINs and OTPs only: 4 to 6 digits, and the original
 * docstring said so outright — "Phone 1234567 -> Phone 1234567 (7 digits, not
 * masked)". That was a deliberate scope, but it is the wrong one for a function
 * called maskSensitiveNumbers and printed on a customer summary. Trace
 * 9876543210 through the old pattern and nothing matches: the lookahead fails
 * at the start because a digit follows, and every later position fails the
 * lookbehind. So a phone number passed through untouched while the year in the
 * same sentence was starred out — exactly backwards.
 *
 * Now:
 *   - 7 or more digits are masked, separators and a leading + included, which
 *     covers phone numbers, account numbers and card numbers. Spaces and
 *     hyphens are tolerated INSIDE such a run because people dictate numbers
 *     that way and the transcript records them that way.
 *   - 4 to 6 digits are still masked, as before, for PINs and one-time codes.
 *   - A four-digit year is left alone. It is not a person, and "in XXXX we
 *     launched" makes a summary harder to read for no gain.
 *
 * Structure is preserved: each digit becomes an X, and separators stay, so the
 * shape of what was said is still legible.
 *
 * <h3>What this is not</h3>
 * This runs in the browser, on a value the API already sent in full. It stops a
 * number being read over somebody's shoulder or pasted out of a summary. It is
 * not access control, and it must not be described as though it were — anyone
 * with the network tab has the original. Masking that actually withholds has to
 * happen server-side, by role.
 *
 * Examples:
 *   "PIN is 1234"              -> "PIN is XXXX"
 *   "Call 9876543210 today"    -> "Call XXXXXXXXXX today"
 *   "Call +91 98765-43210"     -> "Call +XX XXXXX-XXXXX"
 *   "In 2026 we launched"      -> "In 2026 we launched"
 *   "Number 123"               -> "Number 123"
 */

/** A four-digit year, which is not a person. */
const looksLikeYear = (digits: string): boolean => /^(19|20)\d{2}$/.test(digits);

const maskDigits = (run: string): string => run.replace(/\d/g, 'X');

/**
 * A run of 7+ digits, allowing the spaces and hyphens people dictate them with.
 *
 * The {5,} is what keeps this from swallowing two short unrelated numbers
 * either side of a space: the shortest possible match is seven characters, so
 * "Room 12 34" is never read as one number.
 */
const LONG_NUMBER = /(?<!\d)(\+?\d[\d\s-]{5,}\d)(?!\d)/g;

/** A standalone 4-6 digit run — a PIN or a one-time code. */
const SHORT_CODE = /(?<!\d)(\d{4,6})(?=\D|$)/g;

export const maskSensitiveNumbers = (text: string): string => {
  if (!text) return text;

  // Long runs first. They are masked to Xs, which are not digits, so the second
  // pass cannot then find a 4-6 digit code inside something already handled.
  const withoutLong = text.replace(LONG_NUMBER, (run) =>
    run.replace(/\D/g, '').length >= 7 ? maskDigits(run) : run,
  );

  return withoutLong.replace(SHORT_CODE, (run) => (looksLikeYear(run) ? run : maskDigits(run)));
};
/**
 * Parse AI transcript JSON string into structured messages
 * The transcript is a JSON array containing conversation messages between user, AI, and system
 *
 * @param transcriptJson - JSON string from the transcript field
 * @returns Array of transcript messages or null if parsing fails
 */
export const parseTranscript = (transcriptJson: string | null | undefined): TranscriptMessage[] | null => {
  if (!transcriptJson) return null;

  try {
    const parsed = JSON.parse(transcriptJson);
    if (Array.isArray(parsed)) {
      // Filter out BEEP messages and return clean transcript
      // Timestamps are already in epoch format (milliseconds)
      return parsed.filter((msg: any) => msg.content !== 'BEEP');
    }
    return null;
  } catch (error) {
    console.error('Failed to parse transcript:', error);
    return null;
  }
};

/**
 * Error categorization for operational pages (CallLogs, AudioSessions, etc.)
 * Provides structured error information with actionable guidance
 */
export interface CategorizedError {
  type: 'organization_not_found' | 'permission' | 'network' | 'timeout' | 'generic';
  title: string;
  message: string;
  action: string;
  severity: 'error' | 'warning';
  icon: 'alert' | 'lock' | 'wifi' | 'clock' | 'info';
}

/**
 * Categorize API error messages for better user experience
 * 
 * @param error - Error message string from API
 * @returns Categorized error object with type, title, message, action, severity, and icon
 */
/**
 * Whether an API error means "this organization cannot serve this data", which
 * the reader fixes by picking another one rather than by retrying.
 *
 * The single place any of this is matched. It was previously spelled out as
 * `error.includes('Organization not found')` in four separate pages, so
 * correcting the server's message broke each of them independently.
 */
export const isOrganizationScopeError = (error: string | null | undefined): boolean => {
  if (!error) return false;
  const e = error.toLowerCase();
  return e.includes('organization not found')
      || e.includes('no telephony identity')
      || e.includes('select one of its businesses');
};

export const categorizeApiError = (error: string | null): CategorizedError | null => {
  if (!error) return null;
  
  // The organization cannot serve this data — pick a different one.
  //
  // ⚠ Two phrasings, and the pages that key off this type hide their org
  // selector when it does not match. The server used to say "Organization not
  // found" for an organization that EXISTS but is a service provider with no
  // business_id; correcting that message to name its real cause silently broke
  // every page matching the old sentence, which then rendered a bare error with
  // the dropdown removed — telling the reader to select a business while hiding
  // the control that selects one.
  //
  // Both are matched so neither wording strands anyone. Matching server prose at
  // all is the underlying fragility; Transfers.tsx no longer does, and the rest
  // should follow.
  if (isOrganizationScopeError(error)) {
    return {
      type: 'organization_not_found',
      title: 'Select an organization',
      message: error,
      action: 'Select a different organization from the dropdown above.',
      severity: 'warning',
      icon: 'alert'
    };
  }
  
  // Permission/Authorization errors
  if (error.toLowerCase().includes('permission') || 
      error.toLowerCase().includes('authorized') || 
      error.toLowerCase().includes('forbidden') ||
      error.toLowerCase().includes('access denied')) {
    return {
      type: 'permission',
      title: 'Access Denied',
      message: error,
      action: 'You do not have permission to view this data. Please contact your administrator.',
      severity: 'error',
      icon: 'lock'
    };
  }
  
  // Network errors
  if (error.toLowerCase().includes('network') || 
      error.toLowerCase().includes('connection') ||
      error.toLowerCase().includes('unable to connect')) {
    return {
      type: 'network',
      title: 'Connection Error',
      message: 'Unable to connect to the server.',
      action: 'Please check your internet connection and try again.',
      severity: 'error',
      icon: 'wifi'
    };
  }
  
  // Timeout errors
  if (error.toLowerCase().includes('timeout') || error.toLowerCase().includes('timed out')) {
    return {
      type: 'timeout',
      title: 'Request Timeout',
      message: 'The request took too long to complete.',
      action: 'Please try again. If the problem persists, try selecting a shorter date range.',
      severity: 'error',
      icon: 'clock'
    };
  }
  
  // Generic error
  return {
    type: 'generic',
    title: 'Error',
    message: error,
    action: 'Please try again or contact support if the problem persists.',
    severity: 'error',
    icon: 'info'
  };
};